import React, { ChangeEvent } from 'react';
import { Button, Modal, Icon } from 'semantic-ui-react';
import { observer, inject } from 'mobx-react';
import { AuthServiceInterface } from '../services/AuthService';
import { Redirect, Link } from 'react-router-dom';
import EmailVerificationRequiredPage from './EmailVerificationRequiredPage';
import { TermsOfService } from './terms/components/TermsOfService';
import { EMAIL_IS_ALREADY_IN_USE_ERROR } from '../common/Constants';
import TagManagerService from '../services/TagManagerService';
import CookieService from '../services/CookieService';
import AuthPage from '../components/auth/AuthPage';

export interface AuthProps {
  auth?: AuthServiceInterface;
  cookie?: CookieService;
}

export type role = 'SUB_CONTRACTOR' | 'GENERAL_CONTRACTOR' | 'unassigned' | 'ADMINISTRATOR';

interface SignupState {
  role: role;
  email: string;
  password: string;
  confirmPassword: string;
  passwordStrength: number;
  agreeToTermsOfService: boolean;
  openedTermsOfService: boolean;
  errorMessage: string | undefined;
  loading: boolean;
  touched: Array<string>;
  focused: string;
}

const email = 'email';
const password = 'password';
const confirmPassword = 'confirmPassword';

// Feature data for the signup page
const features = [
  {
    icon: 'file alternate outline',
    title: 'Compared to AIA G702',
    description: 'Aligned with industry standards for Industry accepted Pay Apps'
  },
  {
    icon: 'headphones',
    title: 'Live US-based support',
    description: 'Speak with an expert who knows Pay Apps'
  },
  {
    icon: 'copy outline',
    title: 'Multi-project',
    description: 'Unlimited jobs, and storage. No per-app or per-project fees.'
  },
  {
    icon: 'archive',
    title: 'Exclusive Materials Stored function',
    description: 'Calculated automatically for you'
  },
  {
    icon: 'external alternate',
    title: 'One-click PDFs',
    description: 'Shareable, and ready for submission.'
  },
  {
    icon: 'download',
    title: 'Import from Excel',
    description: 'Bring over your SOV/line items in seconds.'
  }
];

@inject('auth')
@inject('cookie')
@observer
export default class SignupPage extends React.Component<AuthProps, SignupState> {

  state: SignupState = {
    role: 'SUB_CONTRACTOR',
    email: '',
    password: '',
    confirmPassword: '',
    passwordStrength: 0,
    agreeToTermsOfService: false,
    openedTermsOfService: false,
    errorMessage: undefined,
    loading: false,
    touched: [],
    focused: ''
  }

  private async signUp(): Promise<void> {
    this.setState({ loading: true });
    if (this.props.auth) {
      try {
        await this.props?.auth.signup(this.state.email, this.state.password, this.state.role, this.props.cookie?.appCookies?.referral as string);
        this.setState({ errorMessage: undefined });
        TagManagerService.logSignupEvent(this.state.email);
      } catch (e) {
        this.setState({ errorMessage: e.message, loading: false });
      }
    }
  }

  invalidEmail(): boolean {
    return this.state.focused !== email
      && this.state.touched.includes(email)
      && this.state.email.match(/^[a-z0-9][a-z0-9+.]*@[a-z0-9-]+(\.[a-z0-9-])+/) === null;
  }

  invalidStrength(): boolean {
    return this.state.password.length > 0 && this.state.touched.includes(password) && this.state.passwordStrength < 2;
  }

  notMatchingPasswords(): boolean {
    return this.state.password !== this.state.confirmPassword;
  }

  onChangeEmail(e: React.ChangeEvent<HTMLInputElement>): void {
    let touched = this.state.touched;
    touched.push(email);
    touched = touched.filter((v, i, a) => a.indexOf(v) === i);
    this.setState({ email: e.target.value.trim(), touched: touched });
  }

  onChangePassword(e: React.ChangeEvent<HTMLInputElement>): void {
    let touched = this.state.touched;
    touched.push(password);
    touched = touched.filter((v, i, a) => a.indexOf(v) === i);

    // Calculate password strength
    const pwd = e.target.value.trim();
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.match(/[a-z]/) && pwd.match(/[A-Z]/)) strength++;
    if (pwd.match(/[0-9]/)) strength++;
    if (pwd.match(/[^a-zA-Z0-9]/)) strength++;
    if (pwd.length >= 12) strength++;

    this.setState({ password: pwd, touched: touched, passwordStrength: strength });
  }

  onFocus(fieldName: string): void {
    this.setState({ focused: fieldName });
  }

  openModal(isOpen: boolean): void {
    return this.setState({ openedTermsOfService: isOpen });
  }

  getStrengthLabel(): string {
    if (this.state.password.length === 0) return '';
    if (this.state.passwordStrength <= 1) return 'Too Short';
    if (this.state.passwordStrength === 2) return 'Weak';
    if (this.state.passwordStrength === 3) return 'Fair';
    if (this.state.passwordStrength === 4) return 'Good';
    return 'Strong';
  }

  getStrengthClass(): string {
    if (this.state.passwordStrength <= 1) return 'weak';
    if (this.state.passwordStrength === 2) return 'weak';
    if (this.state.passwordStrength === 3) return 'fair';
    return 'good';
  }

  renderErrorMessage(): React.ReactNode {
    if (typeof this.state.errorMessage !== 'undefined' && this.state.errorMessage.startsWith(EMAIL_IS_ALREADY_IN_USE_ERROR)) {
      return (
        <div className="auth-error-message">
          {this.state.errorMessage}
          <br />
          <Link to={{ pathname: '/login', state: { email: this.state.email } }}>Log in with this email.</Link>
        </div>
      );
    }

    if (this.invalidEmail()) {
      return <div className="auth-error-message">Invalid email address</div>;
    }

    if (this.invalidStrength()) {
      return <div className="auth-error-message">Weak password. Add another uncommon word or two</div>;
    }

    if (this.notMatchingPasswords() && this.state.confirmPassword.length > 0) {
      return <div className="auth-error-message">Passwords don't match</div>;
    }

    if (this.state.errorMessage) {
      return <div className="auth-error-message">{this.state.errorMessage}</div>;
    }

    return null;
  }

  render(): React.ReactNode {
    if (this.props.auth?.user?.id) {
      setTimeout(() => {
        this.setState({ loading: false });
      });
      if (!this.props.auth.user.emailVerified) {
        return <EmailVerificationRequiredPage firstLogin={true} />;
      }
      return <Redirect to="/" />;
    }

    return (
      <AuthPage pageTitle="Sign Up - PAYearned" showLoginButton={true}>
        <div className="signup-container">
          <h1 className="signup-title">Signup for your FREE account</h1>
          <p className="signup-subtitle">No Credit Card Required</p>

          <div className="signup-content">
            <div className="signup-features">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon-wrapper">
                    <div className="feature-accent-bar"></div>
                    <div className="feature-icon">
                      <Icon name={feature.icon as any} size="big"  />
                    </div>
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="signup-form-wrapper">
              <div className="auth-form-card">
                {this.renderErrorMessage()}

                <div className="auth-form-group">
                  <label className="auth-form-label">Email Address</label>
                  <input
                    type="email"
                    className={`auth-form-input ${this.invalidEmail() ? 'error' : ''}`}
                    placeholder="JohnSmith@email.com"
                    value={this.state.email}
                    onFocus={(): void => this.onFocus(email)}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => this.onChangeEmail(e)}
                  />
                </div>

                <div className="auth-form-group">
                  <label className="auth-form-label">Password</label>
                  <input
                    type="password"
                    className={`auth-form-input ${this.invalidStrength() ? 'error' : ''}`}
                    placeholder="Create a Password"
                    value={this.state.password}
                    onFocus={(): void => this.onFocus(password)}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => this.onChangePassword(e)}
                  />
                  {this.state.password.length > 0 && (
                    <div className="password-strength-wrapper">
                      <div className="password-strength-bars">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`password-strength-bar ${level <= this.state.passwordStrength ? this.getStrengthClass() : ''}`}
                          />
                        ))}
                      </div>
                      <div className="password-strength-text">{this.getStrengthLabel()}</div>
                    </div>
                  )}
                </div>

                <div className="auth-form-group">
                  <label className="auth-form-label">Password</label>
                  <input
                    type="password"
                    className={`auth-form-input ${this.notMatchingPasswords() && this.state.confirmPassword.length > 0 ? 'error' : ''}`}
                    placeholder="Confirm Password"
                    value={this.state.confirmPassword}
                    onFocus={(): void => this.onFocus(confirmPassword)}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => this.setState({ confirmPassword: e.target.value.trim() })}
                  />
                </div>

                <div className="auth-checkbox-group">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={this.state.agreeToTermsOfService}
                    onChange={(e): void => this.setState({ agreeToTermsOfService: e.target.checked })}
                  />
                  <label htmlFor="terms-checkbox">
                    I agree to the <a onClick={(): void => this.openModal(true)}>Terms of Service</a>
                  </label>
                </div>

                <button
                  className="auth-form-button"
                  disabled={
                    !this.state.email ||
                    !this.state.password ||
                    !this.state.confirmPassword ||
                    this.invalidEmail() ||
                    this.state.loading ||
                    this.invalidStrength() ||
                    this.notMatchingPasswords() ||
                    !this.state.agreeToTermsOfService
                  }
                  onClick={(): Promise<void> => this.signUp()}
                >
                  {this.state.loading ? 'Signing up...' : 'Signup'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal
          open={this.state.openedTermsOfService}
          onClose={(): void => this.openModal(false)}
          onOpen={(): void => this.openModal(true)}
        >
          <Modal.Header>Terms of Service</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <TermsOfService />
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button primary onClick={(): void => this.openModal(false)}>
              Close
            </Button>
          </Modal.Actions>
        </Modal>
      </AuthPage>
    );
  }
}
